import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Custom: keeps a variant-scoped product description in sync with the selected variant.
 *
 * On a standard product page the variant picker only morphs the <variant-picker>
 * element; every other variant-dependent element updates itself by listening for
 * the productSelect event and pulling its fresh copy out of the re-fetched section
 * HTML (see media-gallery.js). The product description has no such behaviour in the
 * stock theme because Shopify has no per-variant description, so we add it here for
 * the `custom.variant_description` metafield feature.
 *
 * @extends Component
 */
export class VariantDescription extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Replaces this description with the freshly rendered one for the selected variant.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        // Match by block id so multiple variant descriptions on a page don't clash.
        const selector = this.dataset.blockId
          ? `variant-description[data-block-id="${this.dataset.blockId}"]`
          : 'variant-description';
        const next = html.querySelector(selector);
        if (!next) return;

        this.replaceWith(next);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[variant-description] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('variant-description')) {
  customElements.define('variant-description', VariantDescription);
}
